import type { AccountOwnerMetadata, DispensaryId } from '@gcv/shared';
import type { QueryClient } from '@tanstack/react-query';
import { injectable } from 'inversify';
import { action, makeAutoObservable, runInAction } from 'mobx';

import { BanksApi } from 'api';
import { ACCOUNT_OWNER_METADATA_KEY } from 'hooks/keys';
import { getFiBankStore } from './FiBankStore';
import { getSnackbarStore } from './SnackBarStore';
import { getUserStore } from './UserStore';

// TODO move the bank staff functionality to this store instead of the bank store to clear up dependencies here
@injectable()
export class FiRelationshipManagerStore {
  relationshipManagers: Record<DispensaryId, string> = {};
  accountOwnerMetadata: AccountOwnerMetadata = {} as AccountOwnerMetadata;
  bankApi = new BanksApi();
  queryClient?: QueryClient;

  bankStore = getFiBankStore();
  snackbarStore = getSnackbarStore();
  userStore = getUserStore();

  constructor() {
    makeAutoObservable(this);
  }

  public setQueryClient = action((queryClient: QueryClient) => {
    this.queryClient = queryClient;
  });

  public setAccountOwnerMetadata = action((accountOwnerMetadata: AccountOwnerMetadata) => {
    this.accountOwnerMetadata = accountOwnerMetadata;
  });

  public setRelationshipManagers = action((accountOwnerMetadata: AccountOwnerMetadata) => {
    const managers: Record<DispensaryId, string> = {};
    const entries = Object.entries(accountOwnerMetadata.value.crb_level);

    if (entries.length !== 0 && this.bankStore.staff && this.bankStore.bank.groups) {
      entries?.forEach(([key, value]) => {
        for (const appArea of value) {
          if (appArea.label === 'Relationship Manager') {
            const assigned = this.bankStore.staff.find((s) => s.id === appArea.assigned_user);

            if (assigned) {
              managers[key] = `${assigned.firstName} ${assigned.lastName}`;
            } else {
              const group = this.bankStore.bank.groups.find((g) => g.id === appArea.assigned_group);

              if (group) {
                managers[key] = group.name;
              } else {
                managers[key] = '--';
              }
            }
          }
        }
      });
    }

    runInAction(() => {
      this.relationshipManagers = managers;
    });
  });

  public saveRelationshipManager = action(async (orgId: string, relationshipManagerId: string) => {
    const relationshipManagerData: {
      assigned_user: string | null;
      assigned_group: string | null;
    } = { assigned_user: null, assigned_group: null };

    if (this.bankStore.staff.some((u) => u.id === relationshipManagerId)) {
      const user = this.bankStore.staff.find((s) => s.id === relationshipManagerId);

      if (user) {
        relationshipManagerData.assigned_user = user.id;
      }
    } else if (this.bankStore.bank.groups.some((g) => g.id === relationshipManagerId)) {
      const group = this.bankStore.bank.groups.find((g) => g.id === relationshipManagerId);

      if (group) {
        relationshipManagerData.assigned_group = group.id;
      }
    }

    const updatedOwnerData = await this.bankApi.updateCrbRelationshipManager(
      this.bankStore.bank.id,
      orgId,
      relationshipManagerData
    );

    runInAction(() => {
      this.accountOwnerMetadata = updatedOwnerData;
    });
    this.setRelationshipManagers(updatedOwnerData);

    // Update React Query cache if query client is available
    if (this.queryClient) {
      this.queryClient.setQueryData([ACCOUNT_OWNER_METADATA_KEY, this.bankStore.bank.id], updatedOwnerData);
    }
  });
}

let store: FiRelationshipManagerStore | undefined;

export function getFiRelationshipManagerStore(): FiRelationshipManagerStore {
  if (!store) {
    store = new FiRelationshipManagerStore();
  }

  return store;
}
